import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiX, FiSave, FiSearch, FiBox, FiDollarSign, FiTrendingDown, FiTrendingUp, FiAlertTriangle, FiEye } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, addDoc, doc, updateDoc, getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import PageLayout from '../../components/PageLayout';
import {
  MasterHeaderActions, MasterAddButton, ContentCard, StatsRow, StatCard, StatIcon, StatInfo, StatValue, StatLabel,
  Table, Th, Tr, Td, StatusBadge, SaveButton, FormGrid, FormGroup, Label, Input, Select,
} from '../../components/SharedStyles';
import {
  loadFixedAssets, loadFixedAssetCategories, ensureFixedAssetCategories,
  generateAssetCode, postAcquisition, FUNDING_OPTIONS, updateAssetWithAcquisition,
} from '../../utils/fixedAssetHelper';
import { findAccountByCode } from '../../utils/journalEntryHelper';
import { previewSchedule, appreciationOf, hasAppreciation } from '../../utils/depreciationHelper';

const today = () => new Date().toISOString().split('T')[0];

const STATUS_COLORS = {
  Active: colors.success.main,
  Draft: colors.warning.main,
  FullyDepreciated: colors.info.main,
  Disposed: colors.text.disabled,
};

const emptyForm = () => ({
  assetName: '',
  categoryId: '',
  description: '',
  serialOrTagNo: '',
  location: '',
  acquisitionDate: today(),
  inServiceDate: today(),
  acquisitionCost: '',
  salvageValue: '0',
  depreciationMethod: 'reducing_balance',
  depreciationRate: '15',
  usefulLifeYears: '0',
  fundingMode: 'cash',
  bankId: '',
});

const AssetRegister = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit } = usePagePermissions('fixedAssets', 'assets');
  const { formatCurrency } = useCurrency();

  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  useEscapeClose(showModal, () => setShowModal(false));
  useEscapeClose(!!detail, () => setDetail(null));

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const [assetList, cats] = await Promise.all([
        loadFixedAssets(activeCompany.id),
        loadFixedAssetCategories(activeCompany.id),
      ]);
      setAssets(assetList);
      setCategories(cats);
      const bankSnap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'banks'), orderBy('bankCode', 'asc')));
      setBanks(bankSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(b => b.linkedAccountId));
    } catch (e) {
      console.error('Error loading assets:', e);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setShowModal(true); };

  const openEdit = (asset) => {
    setEditing(asset);
    setForm({
      assetName: asset.assetName || '',
      categoryId: asset.categoryId || '',
      description: asset.description || '',
      serialOrTagNo: asset.serialOrTagNo || '',
      location: asset.locationName || '',
      acquisitionDate: asset.acquisitionDate || today(),
      inServiceDate: asset.inServiceDate || asset.acquisitionDate || today(),
      acquisitionCost: String(asset.acquisitionCost ?? ''),
      salvageValue: String(asset.salvageValue ?? '0'),
      depreciationMethod: asset.depreciationMethod || 'reducing_balance',
      depreciationRate: String(asset.depreciationRate ?? '0'),
      usefulLifeYears: String(asset.usefulLifeYears ?? '0'),
      fundingMode: asset.sourceTransactionId ? (asset.fundingMode && asset.fundingMode !== 'existing' ? asset.fundingMode : 'cash') : 'existing',
      bankId: (banks.find(b => asset.fundingAccountId && b.linkedAccountId === asset.fundingAccountId) || {}).id || '',
    });
    setShowModal(true);
    // Older assets didn't store which bank paid — read it from the acquisition voucher.
    if (asset.sourceTransactionId && asset.fundingMode === 'bank' && !asset.fundingAccountId) {
      getDoc(doc(db, 'companies', activeCompany.id, 'transactions', asset.sourceTransactionId)).then(snap => {
        if (!snap.exists()) return;
        const cr = (snap.data().lines || []).find(l => (Number(l.creditAmount) || 0) > 0);
        const bank = cr && banks.find(b => b.linkedAccountId === cr.accountId);
        if (bank) setForm(f => ({ ...f, bankId: bank.id }));
      }).catch(() => {});
    }
  };

  // When a category is picked, pre-fill depreciation defaults from it.
  const onCategoryChange = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    setForm(f => ({
      ...f,
      categoryId,
      depreciationMethod: cat?.defaultMethod || f.depreciationMethod,
      depreciationRate: String(cat?.defaultRate ?? f.depreciationRate),
      usefulLifeYears: String(cat?.defaultUsefulLifeYears ?? f.usefulLifeYears),
    }));
  };

  const handleSeedCategories = async () => {
    try {
      await ensureFixedAssetCategories(activeCompany.id, currentUser.uid);
      await load();
    } catch (e) {
      alert('Failed to seed categories. Run Repair COA first if accounts are missing.');
    }
  };

  const resolveFundingAccount = async () => {
    if (form.fundingMode === 'existing') return null;
    if (form.fundingMode === 'bank') {
      const bank = banks.find(b => b.id === form.bankId);
      if (!bank?.linkedAccountId) throw new Error('Please select a bank account.');
      const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'accounts', bank.linkedAccountId));
      if (!snap.exists()) throw new Error('Bank account not found in COA.');
      return { id: snap.id, ...snap.data() };
    }
    const opt = FUNDING_OPTIONS.find(o => o.value === form.fundingMode);
    const acct = await findAccountByCode(activeCompany.id, opt.accountCode);
    if (!acct) throw new Error(`Funding account ${opt.accountCode} not found. Run Repair COA.`);
    return acct;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!form.assetName.trim()) { alert('Enter an asset name.'); return; }
    if (!form.categoryId) { alert('Pick a category.'); return; }
    const cost = Number(form.acquisitionCost) || 0;
    if (cost <= 0) { alert('Acquisition cost must be greater than zero.'); return; }
    if (Number(form.salvageValue) >= cost) { alert('Salvage value must be less than cost.'); return; }
    if (form.inServiceDate < form.acquisitionDate) { alert('In-service date cannot be before acquisition date.'); return; }
    if (form.depreciationMethod === 'reducing_balance' && Number(form.depreciationRate) <= 0) { alert('Reducing-balance needs a rate > 0.'); return; }
    if (form.depreciationMethod === 'straight_line' && Number(form.usefulLifeYears) <= 0) { alert('Straight-line needs useful life > 0.'); return; }
    if (!activeFinancialYear?.id && (!editing || (!editing.sourceTransactionId && form.fundingMode !== 'existing'))) { alert('No active financial year. Set one in Settings > Financial Year.'); return; }

    const cat = categories.find(c => c.id === form.categoryId);
    setSaving(true);
    try {
      if (editing) {
        // ACC-23: every field is editable; the acquisition voucher follows the change.
        const fundingAccount = await resolveFundingAccount();
        await updateAssetWithAcquisition(activeCompany.id, {
          oldAsset: editing,
          category: cat,
          fundingMode: form.fundingMode,
          fundingAccount,
          financialYearId: activeFinancialYear?.id || null,
          userId: currentUser.uid,
          fields: {
            assetName: form.assetName.trim(),
            categoryId: form.categoryId,
            description: form.description,
            serialOrTagNo: form.serialOrTagNo,
            locationName: form.location,
            acquisitionDate: form.acquisitionDate,
            inServiceDate: form.inServiceDate,
            acquisitionCost: cost,
            salvageValue: Number(form.salvageValue) || 0,
            depreciationMethod: form.depreciationMethod,
            depreciationRate: Number(form.depreciationRate) || 0,
            usefulLifeYears: Number(form.usefulLifeYears) || 0,
          },
        });
      } else {
        // Resolve funding before creating the record so a bad funding choice
        // doesn't leave an orphan asset.
        const fundingAccount = await resolveFundingAccount();
        const assetCode = await generateAssetCode(activeCompany.id);
        const assetDoc = {
          assetCode,
          assetName: form.assetName.trim(),
          categoryId: form.categoryId,
          categoryName: cat?.categoryName || '',
          assetAccountId: cat?.assetAccountId || null,
          accumDepAccountId: cat?.accumDepAccountId || null,
          description: form.description,
          serialOrTagNo: form.serialOrTagNo,
          locationName: form.location,
          branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
          branchName: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.branchName : null,
          acquisitionDate: form.acquisitionDate,
          inServiceDate: form.inServiceDate,
          acquisitionCost: cost,
          salvageValue: Number(form.salvageValue) || 0,
          depreciationMethod: form.depreciationMethod,
          depreciationRate: Number(form.depreciationRate) || 0,
          usefulLifeYears: Number(form.usefulLifeYears) || 0,
          accumulatedDepreciation: 0,
          netBookValue: cost,
          lastDepreciationDate: null,
          status: 'Active',
          financialYearId: activeFinancialYear.id,
          fundingMode: form.fundingMode,
          fundingAccountId: fundingAccount?.id || null,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.uid,
        };
        const ref = await addDoc(collection(db, 'companies', activeCompany.id, 'fixedAssets'), assetDoc);

        if (form.fundingMode !== 'existing') {
          try {
            const posted = await postAcquisition(activeCompany.id, {
              asset: { ...assetDoc, id: ref.id },
              category: cat,
              fundingAccount,
              financialYearId: activeFinancialYear.id,
              branchId: assetDoc.branchId,
              branchName: assetDoc.branchName,
              userId: currentUser.uid,
            });
            await updateDoc(ref, { sourceTransactionId: posted.transactionId, sourceDocNumber: posted.docNumber });
          } catch (postErr) {
            console.error('Acquisition posting failed:', postErr);
            alert(`Asset saved, but the acquisition entry failed to post: ${postErr.message}. You can post it manually via a Journal Voucher.`);
          }
        }
      }
      setShowModal(false);
      await load();
    } catch (err) {
      console.error('Error saving asset:', err);
      alert(`Failed to save asset: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const filtered = assets.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.assetName || '').toLowerCase().includes(q)
      || (a.assetCode || '').toLowerCase().includes(q)
      || (a.categoryName || '').toLowerCase().includes(q)
      || (a.serialOrTagNo || '').toLowerCase().includes(q);
  });

  const totalCost = assets.reduce((s, a) => s + (Number(a.acquisitionCost) || 0), 0);
  const totalNBV = assets.reduce((s, a) => s + (Number(a.netBookValue) || 0), 0);
  // Asset Appreciation: shown only when some asset has it.
  const showAppr = assets.some(hasAppreciation);
  const totalAppr = assets.reduce((s, a) => s + appreciationOf(a), 0);
  const noCategories = !loading && categories.length === 0;

  return (
    <PageLayout pageTitle="Asset Register" breadcrumb="Fixed Assets / Register">
      <MasterHeaderActions>
        {canEdit && (
          <MasterAddButton onClick={openAdd} disabled={noCategories}>
            <FiPlus /> Add Asset
          </MasterAddButton>
        )}
      </MasterHeaderActions>

      {noCategories && (
        <Banner>
          <FiAlertTriangle />
          <span>No asset categories found. </span>
          {canEdit
            ? <BannerBtn onClick={handleSeedCategories}>Seed default categories</BannerBtn>
            : <span>Ask an admin to seed asset categories.</span>}
        </Banner>
      )}

      <StatsRow>
        <StatCard>
          <StatIcon $color={colors.primary.main}><FiBox /></StatIcon>
          <StatInfo><StatValue>{assets.length}</StatValue><StatLabel>Total Assets</StatLabel></StatInfo>
        </StatCard>
        <StatCard>
          <StatIcon $color={colors.info.main}><FiDollarSign /></StatIcon>
          <StatInfo><StatValue>{formatCurrency(totalCost)}</StatValue><StatLabel>Acquisition Cost</StatLabel></StatInfo>
        </StatCard>
        {showAppr && (
          <StatCard>
            <StatIcon $color={colors.warning.main}><FiTrendingUp /></StatIcon>
            <StatInfo><StatValue>{formatCurrency(totalAppr)}</StatValue><StatLabel>Appreciation</StatLabel></StatInfo>
          </StatCard>
        )}
        <StatCard>
          <StatIcon $color={colors.success.main}><FiTrendingDown /></StatIcon>
          <StatInfo><StatValue>{formatCurrency(totalNBV)}</StatValue><StatLabel>Net Book Value</StatLabel></StatInfo>
        </StatCard>
      </StatsRow>

      <ContentCard>
        <SearchWrap>
          <FiSearch />
          <input placeholder="Search by name, code, category, tag..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </SearchWrap>

        {loading ? (
          <EmptyMsg>Loading assets...</EmptyMsg>
        ) : filtered.length === 0 ? (
          <EmptyMsg>{search ? 'No assets match your search.' : 'No assets yet. Click "Add Asset" to register your first one.'}</EmptyMsg>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Asset</Th>
                <Th>Category</Th>
                <Th>Acquired</Th>
                <Th style={{ textAlign: 'right' }}>Cost</Th>
                {showAppr && <Th style={{ textAlign: 'right' }}>Appreciation</Th>}
                <Th style={{ textAlign: 'right' }}>Accum. Dep.</Th>
                <Th style={{ textAlign: 'right' }}>NBV</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <Tr key={a.id} onClick={() => setDetail(a)} style={{ cursor: 'pointer' }}>
                  <Td><strong>{a.assetCode}</strong></Td>
                  <Td>
                    <div>{a.assetName}</div>
                    {a.serialOrTagNo && <Muted>{a.serialOrTagNo}</Muted>}
                  </Td>
                  <Td>{a.categoryName}</Td>
                  <Td>{a.acquisitionDate}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(a.acquisitionCost || 0)}</Td>
                  {showAppr && <Td style={{ textAlign: 'right' }}>{appreciationOf(a) > 0 ? formatCurrency(appreciationOf(a)) : '—'}</Td>}
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(a.accumulatedDepreciation || 0)}</Td>
                  <Td style={{ textAlign: 'right' }}><strong>{formatCurrency(a.netBookValue ?? a.acquisitionCost ?? 0)}</strong></Td>
                  <Td><StatusBadge style={{ background: `${STATUS_COLORS[a.status] || colors.text.disabled}22`, color: STATUS_COLORS[a.status] || colors.text.secondary }}>{a.status}</StatusBadge></Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <IconBtn onClick={() => setDetail(a)} title="View"><FiEye /></IconBtn>
                    {canEdit && <IconBtn onClick={() => openEdit(a)} title="Edit" style={{ marginLeft: 6 }}><FiEdit2 /></IconBtn>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>

      {showModal && createPortal(
        <Overlay onClick={() => setShowModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editing ? `Edit Asset — ${editing.assetCode}` : 'Add Asset'}</h3>
              <CloseBtn onClick={() => setShowModal(false)}><FiX /></CloseBtn>
            </ModalHeader>
            <form onSubmit={handleSave}>
              <FormGrid>
                <FormGroup>
                  <Label>Asset Name *</Label>
                  <Input value={form.assetName} onChange={(e) => setForm({ ...form, assetName: e.target.value })} autoFocus />
                </FormGroup>
                <FormGroup>
                  <Label>Category *</Label>
                  <Select value={form.categoryId} onChange={(e) => onCategoryChange(e.target.value)}>
                    <option value="">Select category...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Serial / Tag No.</Label>
                  <Input value={form.serialOrTagNo} onChange={(e) => setForm({ ...form, serialOrTagNo: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Acquisition Date *</Label>
                  <Input type="date" value={form.acquisitionDate} onChange={(e) => setForm({ ...form, acquisitionDate: e.target.value, inServiceDate: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>In-Service Date</Label>
                  <Input type="date" value={form.inServiceDate} onChange={(e) => setForm({ ...form, inServiceDate: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Acquisition Cost *</Label>
                  <Input type="number" min="0" step="0.01" value={form.acquisitionCost} onChange={(e) => setForm({ ...form, acquisitionCost: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Salvage Value</Label>
                  <Input type="number" min="0" step="0.01" value={form.salvageValue} onChange={(e) => setForm({ ...form, salvageValue: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Depreciation Method</Label>
                  <Select value={form.depreciationMethod} onChange={(e) => setForm({ ...form, depreciationMethod: e.target.value })}>
                    <option value="reducing_balance">Reducing Balance (WDV)</option>
                    <option value="straight_line">Straight Line</option>
                    <option value="none">No Depreciation</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Annual Rate %</Label>
                  <Input type="number" min="0" max="100" step="0.01" value={form.depreciationRate}
                    disabled={form.depreciationMethod !== 'reducing_balance'}
                    onChange={(e) => setForm({ ...form, depreciationRate: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Useful Life (years)</Label>
                  <Input type="number" min="0" step="1" value={form.usefulLifeYears}
                    disabled={form.depreciationMethod !== 'straight_line'}
                    onChange={(e) => setForm({ ...form, usefulLifeYears: e.target.value })} />
                </FormGroup>
              </FormGrid>

              {(
                <>
                  <SubHeading>Acquisition Posting</SubHeading>
                  <FormGrid>
                    <FormGroup>
                      <Label>Funding</Label>
                      <Select value={form.fundingMode} onChange={(e) => setForm({ ...form, fundingMode: e.target.value })}>
                        {FUNDING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Select>
                    </FormGroup>
                    {form.fundingMode === 'bank' && (
                      <FormGroup>
                        <Label>Bank Account</Label>
                        <Select value={form.bankId} onChange={(e) => setForm({ ...form, bankId: e.target.value })}>
                          <option value="">Select bank...</option>
                          {banks.map(b => <option key={b.id} value={b.id}>{b.bankName}{b.branchName ? ` - ${b.branchName}` : ''}</option>)}
                        </Select>
                      </FormGroup>
                    )}
                  </FormGrid>
                  <Hint>
                    {form.fundingMode === 'existing'
                      ? 'No accounting entry will be posted — use this when the purchase was already recorded via a Payment/Invoice.'
                      : 'Posts a journal: Dr asset account / Cr the funding account above.'}
                  </Hint>
                </>
              )}
              {editing && <Hint>Saving updates the acquisition voucher{editing.sourceDocNumber ? ` (${editing.sourceDocNumber})` : ''} to match. Past depreciation stays on the category it was charged to.</Hint>}

              <ModalFooter>
                <SaveButton type="submit" disabled={saving}><FiSave /> {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Save Asset')}</SaveButton>
              </ModalFooter>
            </form>
          </ModalCard>
        </Overlay>,
        document.body
      )}

      {detail && createPortal(
        <DrawerOverlay onClick={() => setDetail(null)}>
          <Drawer onClick={(e) => e.stopPropagation()}>
            <DrawerHead>
              <div>
                <h3>{detail.assetName}</h3>
                <Muted>{detail.assetCode} · {detail.categoryName}</Muted>
              </div>
              <CloseBtn onClick={() => setDetail(null)}><FiX /></CloseBtn>
            </DrawerHead>

            <InfoGrid>
              <Info><span>Status</span><strong>{detail.status}</strong></Info>
              <Info><span>Location</span><strong>{detail.locationName || '—'}</strong></Info>
              <Info><span>Serial / Tag</span><strong>{detail.serialOrTagNo || '—'}</strong></Info>
              <Info><span>Acquired</span><strong>{detail.acquisitionDate}</strong></Info>
              <Info><span>In Service</span><strong>{detail.inServiceDate || detail.acquisitionDate}</strong></Info>
              <Info><span>Method</span><strong>{detail.depreciationMethod === 'reducing_balance' ? `WDV ${detail.depreciationRate}%` : detail.depreciationMethod === 'straight_line' ? `SLM ${detail.usefulLifeYears}yr` : 'None'}</strong></Info>
              <Info><span>Cost</span><strong>{formatCurrency(detail.acquisitionCost || 0)}</strong></Info>
              <Info><span>Salvage</span><strong>{formatCurrency(detail.salvageValue || 0)}</strong></Info>
              {hasAppreciation(detail) && <Info><span>Appreciation</span><strong>{formatCurrency(appreciationOf(detail))}</strong></Info>}
              <Info><span>Accum. Dep.</span><strong>{formatCurrency(detail.accumulatedDepreciation || 0)}</strong></Info>
              <Info><span>Net Book Value</span><strong>{formatCurrency(detail.status === 'Disposed' ? 0 : (detail.netBookValue ?? detail.acquisitionCost ?? 0))}</strong></Info>
            </InfoGrid>

            {(detail.sourceDocNumber || detail.fundingMode) && (
              <Note>Acquisition: {detail.sourceDocNumber ? <strong>{detail.sourceDocNumber}</strong> : (detail.fundingMode === 'existing' ? 'linked to an existing entry' : 'posted')}</Note>
            )}
            {hasAppreciation(detail) && (
              <Note>
                Appreciation:{' '}
                {(detail.appreciationEntries || []).map((e, i) => (
                  <span key={e.transactionId || i}>{i > 0 ? ' · ' : ''}<strong>{e.docNumber || '—'}</strong> ({e.date}) {formatCurrency(e.amount || 0)}</span>
                ))}
              </Note>
            )}
            {detail.status === 'Disposed' && (
              <Note>
                Disposed ({detail.disposalType}) on <strong>{detail.disposalDate}</strong> · Proceeds {formatCurrency(detail.disposalProceeds || 0)} ·{' '}
                <span style={{ color: (detail.gainLossAmount || 0) >= 0 ? colors.success.main : colors.error.main }}>
                  {(detail.gainLossAmount || 0) >= 0 ? 'Gain' : 'Loss'} {formatCurrency(Math.abs(detail.gainLossAmount || 0))}
                </span>{detail.disposalDocNumber ? ` · ${detail.disposalDocNumber}` : ''}
              </Note>
            )}

            <SubHeading>Depreciation Schedule (projected)</SubHeading>
            {(() => {
              const sched = previewSchedule(detail);
              if (sched.length === 0) return <Muted>No depreciation projected (non-depreciable or fully depreciated).</Muted>;
              return (
                <SchedTable>
                  <thead><tr><th>Year</th><th className="r">Opening</th><th className="r">Depreciation</th><th className="r">Closing</th></tr></thead>
                  <tbody>
                    {sched.map(s => (
                      <tr key={s.year}>
                        <td>{s.year}</td>
                        <td className="r">{formatCurrency(s.opening)}</td>
                        <td className="r">{formatCurrency(s.depreciation)}</td>
                        <td className="r">{formatCurrency(s.closing)}</td>
                      </tr>
                    ))}
                  </tbody>
                </SchedTable>
              );
            })()}
          </Drawer>
        </DrawerOverlay>,
        document.body
      )}
    </PageLayout>
  );
};

const EmptyMsg = styled.div`padding: 40px 20px; text-align: center; color: ${colors.text.secondary}; line-height: 1.6;`;
const Muted = styled.div`font-size: 12px; color: ${colors.text.secondary};`;
const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; margin-bottom: 12px;
  border: 1px solid ${colors.border.light}; border-radius: 8px; color: ${colors.text.secondary};
  input { border: none; outline: none; flex: 1; font-size: 14px; background: transparent; color: ${colors.text.primary}; }
`;
const Banner = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 12px 16px; margin-bottom: 16px;
  background: ${colors.warning.main}1a; border: 1px solid ${colors.warning.main}; border-radius: 8px;
  color: ${colors.warning.dark}; font-size: 14px;
`;
const BannerBtn = styled.button`
  background: ${colors.warning.dark}; color: #fff; border: none; border-radius: 6px; padding: 6px 12px;
  cursor: pointer; font-size: 13px;
`;
const IconBtn = styled.button`
  background: none; border: 1px solid ${colors.border.light}; border-radius: 6px; padding: 6px 8px;
  cursor: pointer; color: ${colors.text.secondary}; display: inline-flex; align-items: center;
  &:hover { color: ${colors.primary.main}; border-color: ${colors.primary.main}; }
`;
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center;
  justify-content: center; z-index: 1000; padding: 16px; overflow-y: auto;
`;
const ModalCard = styled.div`
  background: ${colors.background.paper}; border-radius: 12px; width: 100%; max-width: 760px;
  padding: 20px; box-shadow: 0 20px 50px rgba(0,0,0,0.3); margin: auto;
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
  h3 { margin: 0; color: ${colors.text.primary}; font-size: 18px; }
`;
const CloseBtn = styled.button`background: none; border: none; cursor: pointer; color: ${colors.text.secondary}; font-size: 20px; display: flex;`;
const SubHeading = styled.h4`margin: 18px 0 10px; color: ${colors.text.primary}; font-size: 14px; border-top: 1px solid ${colors.border.light}; padding-top: 14px;`;
const Hint = styled.p`font-size: 12px; color: ${colors.text.secondary}; margin: 10px 0 0;`;
const ModalFooter = styled.div`display: flex; justify-content: flex-end; margin-top: 20px;`;
const DrawerOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; justify-content: flex-end; z-index: 1000;
`;
const Drawer = styled.div`
  width: 100%; max-width: 480px; height: 100%; background: ${colors.background.main}; overflow-y: auto;
  padding: 20px; box-shadow: -10px 0 40px rgba(0,0,0,0.25);
`;
const DrawerHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px;
  padding-bottom: 14px; border-bottom: 1px solid ${colors.border.light};
  h3 { margin: 0; color: ${colors.text.primary}; font-size: 18px; }
`;
const InfoGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 10px;`;
const Info = styled.div`
  display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; background: ${colors.background.paper};
  border-radius: 8px;
  span { font-size: 11px; color: ${colors.text.secondary}; }
  strong { font-size: 14px; color: ${colors.text.primary}; }
`;
const Note = styled.div`
  margin-top: 12px; padding: 10px 12px; background: ${colors.background.paper}; border-radius: 8px;
  font-size: 13px; color: ${colors.text.secondary};
`;
const SchedTable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px;
  th, td { padding: 7px 10px; border-bottom: 1px solid ${colors.border.light}; text-align: left; }
  th { color: ${colors.text.secondary}; font-weight: 600; font-size: 11px; text-transform: uppercase; }
  td.r, th.r { text-align: right; font-family: 'Consolas', monospace; }
`;

export default AssetRegister;
